import React, { FC, MouseEventHandler, ReactElement } from 'react'
import clsx from 'clsx'
import Toolbar from '@material-ui/core/Toolbar'
import { Button } from '@material-ui/core'
import Icon, { IconName } from '../../../../React/Common/Components/Icon'
import { usePermissions, UserPermissions } from '../../../Hooks/usePermissions'
import { AdminBackendPermissionNames } from '../../../../React/Common/Constants/Permissions'
import { isStringNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'

export interface DeleteAddToolbarLocaleText {
    deleteButton: string
    addButton: string
    changeButton: string
    viewButton: string
    selectionActive: (count: number) => React.ReactNode
    /** Label for optional bulk-create/upload control (shown next to Add when {@link DeleteAddToolbarProps.onBulkAddClick} is set). */
    bulkAddButton?: string
}

export const DeleteAddToolbarLocaleTextDefaults: DeleteAddToolbarLocaleText = {
    deleteButton: 'Delete',
    addButton: 'Add',
    changeButton: 'Change',
    viewButton: 'View',
    selectionActive: (count: number): string => `${count} Selected`
}

/**
 * Extra toolbar control shown when rows are selected (between Delete and Change/View).
 * Visibility is controlled by {@link DeleteAddToolbarCustomAction.isVisible} and optionally Update permission.
 */
export type DeleteAddToolbarCustomAction = {
    label: string
    icon?: IconName
    /**
     * Return true to show the button for the current selection count.
     *
     * @param itemsSelectedCount - Number of selected rows.
     */
    isVisible: (itemsSelectedCount: number) => boolean
    /**
     * When true (default), the user must have Update on the table `permissionName` for the action to show.
     * Set to false for actions that are not gated on Update (e.g. Sign in as).
     */
    requiresUpdatePermission?: boolean
    onClick: MouseEventHandler<HTMLButtonElement>
}

interface DeleteAddToolbarProps {
    itemsSelectedCount: number
    localeText: DeleteAddToolbarLocaleText
    onAddClick?: MouseEventHandler<HTMLButtonElement>
    /** Optional bulk insert/upload wizard entry point (Create permission); shown beside Add when no rows are selected. */
    onBulkAddClick?: MouseEventHandler<HTMLButtonElement>
    onDeleteClick?: MouseEventHandler<HTMLButtonElement>
    onChangeClick?: MouseEventHandler<HTMLButtonElement>
    onViewClick?: MouseEventHandler<HTMLButtonElement>
    /** Optional row-selection actions rendered after Delete and before Change/View. */
    customActions?: DeleteAddToolbarCustomAction[]
    permissionName?: AdminBackendPermissionNames
    /** When true, the Add button is disabled. */
    addButtonDisabled?: boolean
    /** Tooltip shown when Add button is disabled (on hover or focus). */
    addButtonDisabledTitle?: string
    /** When true, the Bulk Add control is disabled (e.g. invalid list filters). */
    bulkAddButtonDisabled?: boolean
    /** Tooltip when Bulk Add is disabled. */
    bulkAddButtonDisabledTitle?: string
}

/**
 * DeleteAddToolbar component.
 *
 * @param {DeleteAddToolbarProps} props - Component properties.
 * @returns {ReactElement} The rendered component.
 */
export const DeleteAddToolbar: FC<DeleteAddToolbarProps> = ({
    itemsSelectedCount,
    onAddClick,
    onBulkAddClick,
    onDeleteClick,
    localeText,
    onChangeClick,
    onViewClick,
    customActions,
    permissionName,
    addButtonDisabled,
    addButtonDisabledTitle,
    bulkAddButtonDisabled,
    bulkAddButtonDisabledTitle
}: DeleteAddToolbarProps): ReactElement => {
    const userPermissions: UserPermissions = usePermissions(permissionName)

    return (
        <div className={clsx('delete-add-toolbar', itemsSelectedCount > 0 && 'delete-add-toolbar-has-selection')}>
            <Toolbar>
                {itemsSelectedCount > 0 ? (
                    <>
                        <b>{localeText.selectionActive(itemsSelectedCount)}</b>
                        <div className='delete-change-buttons-container'>
                            {userPermissions?.Delete && onDeleteClick ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.DELETE} />}
                                    onClick={onDeleteClick}
                                >
                                    {localeText.deleteButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                            {(customActions ?? []).map(
                                (action: DeleteAddToolbarCustomAction, index: number): OnlyNullable<ReactElement> => {
                                    const requiresUpdate: boolean = action.requiresUpdatePermission !== false

                                    if (
                                        !action.isVisible(itemsSelectedCount) ||
                                        (requiresUpdate && !userPermissions?.Update)
                                    ) {
                                        return null
                                    }

                                    return (
                                        <Button
                                            key={`toolbar-custom-${action.label}-${String(index)}`}
                                            variant='contained'
                                            color='default'
                                            startIcon={action.icon ? <Icon iconName={action.icon} /> : undefined}
                                            onClick={action.onClick}
                                        >
                                            {action.label}
                                        </Button>
                                    )
                                }
                            )}
                            {itemsSelectedCount === 1 && userPermissions?.Update ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.CHANGE} />}
                                    onClick={onChangeClick}
                                >
                                    {localeText.changeButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                            {itemsSelectedCount === 1 && userPermissions?.Read && !userPermissions?.Update ? (
                                <Button
                                    variant='contained'
                                    color='default'
                                    startIcon={<Icon iconName={IconName.PREVIEW} />}
                                    onClick={onViewClick}
                                >
                                    {localeText.viewButton}
                                </Button>
                            ) : (
                                <></>
                            )}
                        </div>
                    </>
                ) : (
                    <></>
                )}
                {!itemsSelectedCount && userPermissions?.Create ? (
                    <div className='delete-add-toolbar-primary-actions'>
                        <Button
                            variant='contained'
                            color='primary'
                            startIcon={<Icon iconName={IconName.ADD} />}
                            onClick={addButtonDisabled ? undefined : onAddClick}
                            disabled={addButtonDisabled}
                            title={addButtonDisabled ? addButtonDisabledTitle : undefined}
                        >
                            {localeText.addButton}
                        </Button>
                        {onBulkAddClick && isStringNotEmpty(localeText.bulkAddButton) ? (
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<Icon iconName={IconName.ADD_ALL} />}
                                onClick={bulkAddButtonDisabled ? undefined : onBulkAddClick}
                                disabled={bulkAddButtonDisabled}
                                title={bulkAddButtonDisabled ? bulkAddButtonDisabledTitle : undefined}
                            >
                                {localeText.bulkAddButton}
                            </Button>
                        ) : (
                            <></>
                        )}
                    </div>
                ) : (
                    <></>
                )}
            </Toolbar>
        </div>
    )
}
